/*
 * COPYRIGHT (c) 2024
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect, useRef, useState } from 'react';

import './BasicConfigurationTheme.scss';
import { useTranslation } from '@scuf/localization';
import {
  Accordion,
  Badge,
  Button,
  Checkbox,
  Icon,
  Popup,
  Select
} from '@scuf/common';

import descriptors from '../../../../locales/en-US/descriptors';
import { DateRangeWrapper } from '../../../shared/DateRangeWrapper/DateRangeWrapper';
import { IndependentVariablesComponent } from '../../../shared/CreateViewBaseline/BasicConfiguration/IndependentVariables/IndependentVarsComponent';
import { IIndependentVarProps } from '../../../shared/CreateViewBaseline/BasicConfiguration/IndependentVariables/Model';
import { IAny } from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { useBaselineConfigContext } from '../../../../context/BaselineConfigContext/BaselineConfigContext';
import BaselineConfigActions from '../../../../context/BaselineConfigContext/BaselineConfigActions';
import { CheckboxIndependentVar } from '../../../shared/CreateViewBaseline/BasicConfiguration/IndependentVariables/Checkbox/CheckboxVariable';

export interface UtilityBasicConfigurationProps {
  onSubmit?: (f: FormData) => Promise<void>;
  utilityType: 'electricity' | 'gas';
  viewMode: 'create' | 'view';
}

const electricityIndepVars: Omit<
  IIndependentVarProps<IAny>,
  'onCheckBoxChange' | 'inputOnChange' | 'checked' | 'disabled' | 'inputValue'
>[] = [
  {
    displayName: 'HDD',
    name: 'hdd',
    type: 'dropdown',
    allowedValues: [
      { text: '61', value: '61' },
      { text: '62', value: '62' },
      { text: '63', value: '63' },
      { text: '64', value: '64' },
      { text: '65', value: '65' },
      { text: '66', value: '66' },
      { text: '67', value: '67' }
    ],
    inputLabel: 'Base Temperature',
    inputUnit: '°F',
    hasTStat: true
  },
  {
    displayName: 'CDD',
    name: 'cdd',
    type: 'dropdown',
    allowedValues: [
      { text: '61', value: '61' },
      { text: '62', value: '62' },
      { text: '63', value: '63' },
      { text: '64', value: '64' },
      { text: '65', value: '65' },
      { text: '66', value: '66' },
      { text: '67', value: '67' }
    ],
    inputLabel: 'Base Temperature',
    inputUnit: '°F',
    hasTStat: true
  }
];

const electricityDefaults = {
  utilityDataType: { inputValue: 'calendar-month' },
  hdd: { checked: true, inputValue: '65' },
  cdd: { checked: true, inputValue: '65' }
};

export const UtilityBasicConfiguration: React.FC<UtilityBasicConfigurationProps> = (
  props: UtilityBasicConfigurationProps
) => {
  const scrollableRef = useRef(null);

  const [t] = useTranslation();
  const baselineConfigDesc = descriptors.baseline;

  // Accordion State all open
  const [allActive, setAllActive] = useState<boolean | undefined>(true);
  const notAllActive = () => {
    setAllActive(undefined);
  };

  const [autoSelectBest, setAutoSelectBest] = useState<boolean>(true);

  const independentVariables =
    props.utilityType === 'electricity' ? electricityIndepVars : [];
  const [state, dispatch] = useBaselineConfigContext();

  const updateInputValue = (
    key: string,
    inputValue: string | { from: Date; to: Date }
  ) => {
    dispatch(
      BaselineConfigActions.updateFormData({ key, value: { inputValue } })
    );
  };

  const updateCheckedValue = (key: string, checked: boolean) => {
    dispatch(BaselineConfigActions.updateFormData({ key, value: { checked } }));
  };
  const [allDisabled, setAllDisabled] = useState<boolean>(false);
  useEffect(() => {
    const loadData = async () => {
      // fetch data from api and load it into the form context
      // else default
      dispatch(
        BaselineConfigActions.initializeFormData({
          baselinePeriod: { inputValue: null },
          reportingPeriod: { inputValue: null },
          ...(props.utilityType === 'electricity' ? electricityDefaults : {})
        })
      );
    };
    loadData();

    return () => {
      dispatch(BaselineConfigActions.resetFormData());
    };
  }, []);

  useEffect(() => {
    setAllDisabled(props.viewMode === 'view');
  }, [props.viewMode]);

  return (
    <div className="basic-configuration-wrapper display-flex-column">
      <div className="basic-configuration-header">
        <div className="title">
          {t(baselineConfigDesc.baselineconfiguration)}
        </div>
      </div>
      {/* <div className="divider-separator"></div> */}
      <div className="baseline-configuration-form-wrapper display-flex-column">
        <div className="baseline-configuration-form" ref={scrollableRef}>
          <div>
            <Accordion className="baseline-configuration-form-accordion">
              <Accordion.Content
                active={allActive}
                onClick={notAllActive}
                className="establish-baseline-accordion basic-config-common-accordion-content"
                title={
                  <>
                    <span className="title-badge-wrapper">
                      <Badge>1</Badge>
                    </span>
                    <span>{t(baselineConfigDesc.estBaselinePeriod)}</span>
                  </>
                }
              >
                <div className="accordion-content-description">
                  {t(baselineConfigDesc.baselinePeriodDescription)}
                </div>
                <div className="accordion-content-input-wrapper">
                  <div className=" common-input-wrapper baseline-period daterange-wrapper">
                    <div className="label">
                      <span>{t(baselineConfigDesc.baselinePeriod)}</span>
                      <span className="label-info-icon">
                        <Popup
                          on="hover"
                          element={
                            <Icon
                              root="common"
                              name="badge-info"
                              size="small"
                            />
                          }
                        ></Popup>
                      </span>
                    </div>
                    <DateRangeWrapper
                      disabled={allDisabled}
                      disableFuture
                      displayFormat="DD MMM YY"
                      type="daterange"
                      showYearSelector
                      id="baseline-period"
                      className="baseline-config-date-picker"
                      fluid
                      rangeValue={state.formData['baselinePeriod']?.inputValue}
                      reserveSpace={false}
                      scrollableParentRef={scrollableRef}
                      onRangeSelect={(range) =>
                        updateInputValue('baselinePeriod', range)
                      }
                    />
                  </div>
                  <div className="common-input-wrapper reporting-period daterange-wrapper">
                    <div className="label">
                      <span>{t(baselineConfigDesc.reportingPeriod)}</span>
                      <span className="label-info-icon">
                        <Popup
                          on="hover"
                          element={
                            <Icon
                              root="common"
                              name="badge-info"
                              size="small"
                            />
                          }
                        ></Popup>
                      </span>
                    </div>
                    <DateRangeWrapper
                      disabled={allDisabled}
                      displayFormat="DD MMM YY"
                      type="daterange"
                      showYearSelector
                      id="reporting-period"
                      className="baseline-config-date-picker"
                      fluid
                      rangeValue={state.formData['reportingPeriod']?.inputValue}
                      reserveSpace={false}
                      scrollableParentRef={scrollableRef}
                      onRangeSelect={(range) =>
                        updateInputValue('reportingPeriod', range)
                      }
                    />
                  </div>
                  <div className="common-input-wrapper">
                    <div className="label">
                      <span>{t(baselineConfigDesc.utilityDataType)}</span>
                    </div>
                    <Select
                      disabled={allDisabled}
                      className="baseline-config-dropdown"
                      options={[
                        {
                          text: t(baselineConfigDesc.calendarMonth),
                          value: 'calendar-month'
                        },
                        {
                          text: t(baselineConfigDesc.billingPeriod),
                          value: 'billing-period'
                        }
                      ]}
                      fluid
                      defaultValue={'calendar-month'}
                      value={state.formData['utilityDataType']?.value}
                      onChange={(value) => {
                        updateInputValue('utilityDataType', value);
                      }}
                    />
                  </div>
                </div>
              </Accordion.Content>
              <Accordion.Content
                active={allActive}
                onClick={notAllActive}
                className="independent-variables-accordion basic-config-common-accordion-content"
                title={
                  <>
                    <span className="title-badge-wrapper">
                      <Badge>2</Badge>
                    </span>
                    <span>{t(baselineConfigDesc.independentVariables)}</span>
                  </>
                }
              >
                <div className="accordion-content-description">
                  {t(baselineConfigDesc.baselinePeriodDescription)}
                </div>
                <div className="accordion-content-input-wrapper">
                  <div className="common-input-wrapper">
                    <CheckboxIndependentVar
                      disabled={allDisabled}
                      displayName={t(baselineConfigDesc.autoSelectBest)}
                      checked={autoSelectBest}
                      onCheckBoxChange={setAutoSelectBest}
                      name="autoSelectBest"
                      type="checkbox"
                    />
                  </div>
                  <IndependentVariablesComponent
                    independentVariables={independentVariables.map((ele) => ({
                      ...ele,
                      disabled: autoSelectBest || allDisabled,
                      checked: state.formData[ele.name]
                        ? state.formData[ele.name].checked
                        : true,
                      inputValue: state.formData[ele.name]?.inputValue,
                      onCheckBoxChange: (checked) =>
                        updateCheckedValue(ele.name, checked),
                      inputOnChange: (inputValue) =>
                        updateInputValue(ele.name, inputValue),
                      inputDisabled: allDisabled
                    }))}
                  />
                </div>
              </Accordion.Content>
            </Accordion>
          </div>
        </div>
        {!allDisabled && (
          <div className="apply-button-wrapper display-flex-column">
            <Button type="secondary" content="Apply" disabled minWidth="100%" />
          </div>
        )}
      </div>
    </div>
  );
};
